import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { evaluationApi, lpoApi, requisitionApi, supplierApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";
import type { LPOLine } from "../../types";

type LineRow = Omit<LPOLine, "id" | "netAmount">;

const MANUAL_SUPPLIER = "manual";

const emptyLine = (order: number): LineRow => ({
  lineOrder: order,
  itemSpec: "",
  unitMeasure: "",
  quantity: 1,
  rate: 0,
  discountPct: 0,
  itemType: "CONSUMABLE",
  inventoryItemId: null,
  assetCategoryId: null,
});

function calcNet(line: LineRow): number {
  return line.quantity * line.rate * (1 - (line.discountPct ?? 0) / 100);
}

export default function LPOCreatePage() {
  const navigate = useNavigate();

  const { data: requisitions, loading: reqsLoading } = useAsyncData(
    () => requisitionApi.list({ size: 100 }).then((r) => r.content),
    [],
  );
  const { data: supplierOptions, loading: suppliersLoading } = useAsyncData(
    () => supplierApi.options(),
    [],
  );
  const { data: approvedEvaluations, loading: evalsLoading } = useAsyncData(
    () => evaluationApi.list({ size: 100 }).then((r) => r.content.filter((e) => e.status === "APPROVED")),
    [],
  );

  const [evaluationId, setEvaluationId] = useState<number | "">("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [requisitionId, setRequisitionId] = useState<number | "">("");
  const [supplierChoice, setSupplierChoice] = useState<number | typeof MANUAL_SUPPLIER | "">("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierAddress, setSupplierAddress] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [lines, setLines] = useState<LineRow[]>([emptyLine(1)]);

  async function handleEvaluationChange(value: string) {
    const id = value ? Number(value) : "";
    setEvaluationId(id);
    if (id === "") return;
    setDraftLoading(true);
    try {
      const draft = await evaluationApi.lpoDraft(id as number);
      if (draft.requisitionId) setRequisitionId(draft.requisitionId);
      if (draft.supplierId) {
        setSupplierChoice(draft.supplierId);
        setSupplierName(draft.supplierName ?? "");
      } else if (draft.supplierName) {
        setSupplierChoice(MANUAL_SUPPLIER);
        setSupplierName(draft.supplierName);
      }
      if (draft.lines.length) {
        setLines(draft.lines.map((l, i) => ({
          lineOrder: l.lineOrder ?? i + 1,
          itemSpec: l.itemSpec,
          unitMeasure: l.unitMeasure ?? "",
          quantity: l.quantity,
          rate: l.rate ?? 0,
          discountPct: l.discountPct ?? 0,
          itemType: l.itemType ?? "CONSUMABLE",
          inventoryItemId: l.inventoryItemId ?? null,
          assetCategoryId: l.assetCategoryId ?? null,
        })));
      }
    } finally {
      setDraftLoading(false);
    }
  }

  async function handleSupplierChoiceChange(value: string) {
    if (value === "" ) {
      setSupplierChoice("");
      setSupplierName("");
      return;
    }
    if (value === MANUAL_SUPPLIER) {
      setSupplierChoice(MANUAL_SUPPLIER);
      return;
    }
    const id = Number(value);
    setSupplierChoice(id);
    // supplierApi.options() is a lightweight {id,name,email} dropdown list — it doesn't carry
    // address, so fetch the full record to auto-fill it (falls back to the option's name only
    // if that lookup fails, same as before this endpoint existed properly).
    const option = (supplierOptions ?? []).find((s) => s.id === id);
    setSupplierName(option?.name ?? "");
    try {
      const full = await supplierApi.get(id);
      setSupplierName(full.name);
      if (full.address) setSupplierAddress(full.address);
    } catch {
      // Best-effort auto-fill only — the fields remain manually editable either way.
    }
  }

  const { mutate, loading, error } = useMutation(async () => {
    if (!supplierName.trim()) throw new Error("Supplier name is required.");
    if (!deliveryDate) throw new Error("Delivery date is required.");
    if (!paymentTerms.trim()) throw new Error("Payment terms are required.");
    const result = await lpoApi.create({
      requisitionId: requisitionId !== "" ? (requisitionId as number) : null,
      evaluationId: evaluationId !== "" ? (evaluationId as number) : null,
      supplierId: typeof supplierChoice === "number" ? supplierChoice : null,
      supplierName,
      supplierAddress: supplierAddress || null,
      deliveryDate,
      paymentTerms,
      lines: lines.map((l) => ({
        lineOrder: l.lineOrder, itemSpec: l.itemSpec, unitMeasure: l.unitMeasure, quantity: l.quantity, rate: l.rate, discountPct: l.discountPct,
        itemType: l.itemType ?? "CONSUMABLE", inventoryItemId: l.inventoryItemId ?? undefined, assetCategoryId: l.assetCategoryId ?? undefined,
      })),
    });
    navigate(`/procurement/lpos/${result.id}`);
  });

  function addLine() {
    setLines((prev) => [...prev, emptyLine(prev.length + 1)]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineOrder: i + 1 })));
  }

  function updateLine(idx: number, field: keyof LineRow, value: string | number) {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  const subtotal = lines.reduce((sum, l) => sum + calcNet(l), 0);
  const vat = subtotal * 0.16;
  const total = subtotal + vat;
  const loadingOptions = reqsLoading || suppliersLoading || evalsLoading;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/lpos" className="text-sm text-emerald-700 hover:underline">&larr; Back to LPOs</Link>
      </div>

      <ProcForm formNo="PROC-FRM-04" title="Local Purchase Order">
        {loadingOptions && <ProcLoading label="Loading form options..." />}
        {error && <ProcError message={error} />}

        <ProcSection title="Create From Evaluation (optional)">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Approved Evaluation</label>
          <select
            value={evaluationId}
            onChange={(e) => handleEvaluationChange(e.target.value)}
            className="w-full rounded-xl border px-3 py-2 text-sm"
          >
            <option value="">— none, fill in manually —</option>
            {(approvedEvaluations ?? []).map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.serialNo} — {ev.recommendedSupplierName ?? "no recommendation"}</option>
            ))}
          </select>
          {draftLoading && <p className="mt-1 text-xs text-slate-400">Loading draft from evaluation…</p>}
          <p className="mt-1 text-xs text-slate-400">Selecting an approved evaluation pre-fills the supplier, requisition, and lines below. Everything remains editable before saving.</p>
        </ProcSection>

        <ProcSection title="Order Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Linked Requisition (optional)</label>
              <select
                value={requisitionId}
                onChange={(e) => setRequisitionId(e.target.value ? Number(e.target.value) : "")}
                className="w-full rounded-xl border px-3 py-2 text-sm"
              >
                <option value="">— none —</option>
                {(requisitions ?? []).map((r) => (
                  <option key={r.id} value={r.id}>{r.serialNo} — {r.purpose}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Supplier *</label>
              <select
                value={supplierChoice}
                onChange={(e) => handleSupplierChoiceChange(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 text-sm"
              >
                <option value="">— select supplier —</option>
                {(supplierOptions ?? []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.email ? ` — ${s.email}` : ""}</option>
                ))}
                <option value={MANUAL_SUPPLIER}>Other (type manually)</option>
              </select>
              {supplierChoice === MANUAL_SUPPLIER && (
                <input
                  type="text"
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  placeholder="Supplier name"
                  className="mt-2 w-full rounded-xl border px-3 py-2 text-sm"
                  required
                />
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Supplier Address</label>
              <textarea value={supplierAddress} onChange={(e) => setSupplierAddress(e.target.value)} rows={2} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Delivery Date *</label>
              <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Payment Terms *</label>
              <input type="text" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="30 days net / on delivery" className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
          </div>
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left w-8">#</th>
                  <th className="px-2 py-2 text-left">Item Spec</th>
                  <th className="px-2 py-2 text-left w-28">Item Type</th>
                  <th className="px-2 py-2 text-left w-24">Unit</th>
                  <th className="px-2 py-2 text-right w-20">Qty</th>
                  <th className="px-2 py-2 text-right w-28">Rate</th>
                  <th className="px-2 py-2 text-right w-20">Disc%</th>
                  <th className="px-2 py-2 text-right w-32">Net Amount</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((line, idx) => (
                  <tr key={idx}>
                    <td className="px-2 py-2 text-slate-400">{line.lineOrder}</td>
                    <td className="px-2 py-2">
                      <input type="text" value={line.itemSpec} onChange={(e) => updateLine(idx, "itemSpec", e.target.value)} placeholder="Description" className="w-full rounded border px-2 py-1 text-xs" />
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={line.itemType ?? "CONSUMABLE"}
                        onChange={(e) => updateLine(idx, "itemType", e.target.value)}
                        className="w-full rounded border px-2 py-1 text-xs"
                      >
                        <option value="CONSUMABLE">Consumable</option>
                        <option value="CAPITAL_ASSET">Capital Asset</option>
                        <option value="SERVICE">Service</option>
                        <option value="WORKS">Works</option>
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input type="text" value={line.unitMeasure} onChange={(e) => updateLine(idx, "unitMeasure", e.target.value)} placeholder="Pcs" className="w-full rounded border px-2 py-1 text-xs" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" value={line.quantity} min={0} onChange={(e) => updateLine(idx, "quantity", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" value={line.rate} min={0} step={0.01} onChange={(e) => updateLine(idx, "rate", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" value={line.discountPct ?? 0} min={0} max={100} onChange={(e) => updateLine(idx, "discountPct", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                    </td>
                    <td className="px-2 py-2 text-right font-semibold text-slate-700 text-xs">
                      {calcNet(line).toLocaleString("en-KE", { style: "currency", currency: "KES" })}
                    </td>
                    <td className="px-2 py-2">
                      <button type="button" onClick={() => removeLine(idx)} disabled={lines.length === 1} className="text-red-400 hover:text-red-600 disabled:opacity-30 text-xs">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 text-sm font-semibold">
                <tr>
                  <td colSpan={7} className="px-2 py-2 text-right text-slate-600">Subtotal:</td>
                  <td className="px-2 py-2 text-right">{subtotal.toLocaleString("en-KE", { style: "currency", currency: "KES" })}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={7} className="px-2 py-2 text-right text-slate-600">VAT (16%):</td>
                  <td className="px-2 py-2 text-right">{vat.toLocaleString("en-KE", { style: "currency", currency: "KES" })}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={7} className="px-2 py-2 text-right text-emerald-700 font-bold">Total:</td>
                  <td className="px-2 py-2 text-right text-emerald-700 font-bold">{total.toLocaleString("en-KE", { style: "currency", currency: "KES" })}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <button type="button" onClick={addLine} className="mt-3 rounded-lg border border-emerald-600 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
            + Add Line
          </button>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/lpos" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save LPO"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
